import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { patientFormApi } from '@/services/patientFormApi';
import type { FormConfig, FormSection, FormField } from '@/types/patientForm';

interface FieldEditorModal {
  isOpen: boolean;
  mode: 'add' | 'edit';
  sectionId: string;
  field?: FormField;
}

interface SectionEditorModal {
  isOpen: boolean;
  mode: 'add' | 'edit';
  section?: FormSection;
}

export default function PatientFormBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [formConfig, setFormConfig] = useState<FormConfig>({ sections: [] });
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  // Field editor modal
  const [fieldEditor, setFieldEditor] = useState<FieldEditorModal>({
    isOpen: false,
    mode: 'add',
    sectionId: '',
  });
  const [editingField, setEditingField] = useState<Partial<FormField>>({
    id: '',
    label: '',
    type: 'text',
    required: false,
    order: 1,
  });

  // Section editor modal
  const [sectionEditor, setSectionEditor] = useState<SectionEditorModal>({
    isOpen: false,
    mode: 'add',
  });
  const [editingSection, setEditingSection] = useState<Partial<FormSection>>({
    id: '',
    title: '',
    description: '',
    order: 1,
    collapsible: false,
    collapsed_by_default: false,
    fields: [],
  });

  useEffect(() => {
    if (id && id !== 'new') {
      loadTemplate();
    } else {
      setLoading(false);
    }
  }, [id]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const template = await patientFormApi.getTemplate(Number(id));
      setTemplateName(template.name);
      setTemplateDesc(template.description);
      setFormConfig(template.form_config);
    } catch (err: any) {
      console.error('Error loading template:', err);
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter template name');
      return;
    }

    if (formConfig.sections.length === 0) {
      toast.error('Please add at least one section');
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: templateName,
        description: templateDesc,
        form_config: formConfig,
        is_active: true,
      };

      if (id && id !== 'new') {
        await patientFormApi.updateTemplate(Number(id), data);
        toast.success('Template updated successfully');
      } else {
        const newTemplate = await patientFormApi.createTemplate(data);
        toast.success('Template created successfully');
        navigate(`/pathology/patients/form-builder/${newTemplate.id}`);
      }
    } catch (err: any) {
      console.error('Error saving template:', err);
      toast.error(err.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Section operations
  const openAddSectionModal = () => {
    const maxOrder = formConfig.sections.length > 0
      ? Math.max(...formConfig.sections.map(s => s.order))
      : 0;

    setEditingSection({
      id: '',
      title: '',
      description: '',
      order: maxOrder + 1,
      collapsible: false,
      collapsed_by_default: false,
      fields: [],
    });
    setSectionEditor({ isOpen: true, mode: 'add' });
  };

  const openEditSectionModal = (section: FormSection) => {
    setEditingSection(section);
    setSectionEditor({ isOpen: true, mode: 'edit', section });
  };

  const saveSection = () => {
    if (!editingSection.id || !editingSection.title) {
      toast.error('Please enter section ID and title');
      return;
    }

    const section: FormSection = {
      id: editingSection.id,
      title: editingSection.title,
      description: editingSection.description || '',
      order: editingSection.order || 1,
      collapsible: editingSection.collapsible || false,
      collapsed_by_default: editingSection.collapsed_by_default || false,
      fields: editingSection.fields || [],
    };

    if (sectionEditor.mode === 'add') {
      setFormConfig(prev => ({
        sections: [...prev.sections, section].sort((a, b) => a.order - b.order),
      }));
      toast.success('Section added');
    } else {
      setFormConfig(prev => ({
        sections: prev.sections.map(s =>
          s.id === sectionEditor.section?.id ? section : s
        ),
      }));
      toast.success('Section updated');
    }

    setSectionEditor({ isOpen: false, mode: 'add' });
  };

  const deleteSection = (sectionId: string) => {
    if (!confirm('Are you sure you want to delete this section?')) return;

    setFormConfig(prev => ({
      sections: prev.sections.filter(s => s.id !== sectionId),
    }));
    toast.success('Section deleted');
  };

  const moveSectionUp = (sectionId: string) => {
    setFormConfig(prev => {
      const sections = [...prev.sections];
      const index = sections.findIndex(s => s.id === sectionId);
      if (index > 0) {
        [sections[index - 1], sections[index]] = [sections[index], sections[index - 1]];
        sections.forEach((s, i) => { s.order = i + 1; });
      }
      return { sections };
    });
  };

  const moveSectionDown = (sectionId: string) => {
    setFormConfig(prev => {
      const sections = [...prev.sections];
      const index = sections.findIndex(s => s.id === sectionId);
      if (index < sections.length - 1) {
        [sections[index], sections[index + 1]] = [sections[index + 1], sections[index]];
        sections.forEach((s, i) => { s.order = i + 1; });
      }
      return { sections };
    });
  };

  // Field operations
  const openAddFieldModal = (sectionId: string) => {
    const section = formConfig.sections.find(s => s.id === sectionId);
    const maxOrder = section && section.fields.length > 0
      ? Math.max(...section.fields.map(f => f.order))
      : 0;

    setEditingField({
      id: '',
      label: '',
      type: 'text',
      required: false,
      order: maxOrder + 1,
    });
    setFieldEditor({ isOpen: true, mode: 'add', sectionId });
  };

  const openEditFieldModal = (sectionId: string, field: FormField) => {
    setEditingField(field);
    setFieldEditor({ isOpen: true, mode: 'edit', sectionId, field });
  };

  const saveField = () => {
    if (!editingField.id || !editingField.label) {
      toast.error('Please enter field ID and label');
      return;
    }

    const field: FormField = {
      id: editingField.id,
      label: editingField.label,
      type: editingField.type || 'text',
      required: editingField.required || false,
      order: editingField.order || 1,
      placeholder: editingField.placeholder,
      help_text: editingField.help_text,
      default_value: editingField.default_value,
      validation: editingField.validation,
      options: editingField.options,
      multiple: editingField.multiple,
      rows: editingField.rows,
      accept: editingField.accept,
    };

    if (fieldEditor.mode === 'add') {
      setFormConfig(prev => ({
        sections: prev.sections.map(section =>
          section.id === fieldEditor.sectionId
            ? {
                ...section,
                fields: [...section.fields, field].sort((a, b) => a.order - b.order),
              }
            : section
        ),
      }));
      toast.success('Field added');
    } else {
      setFormConfig(prev => ({
        sections: prev.sections.map(section =>
          section.id === fieldEditor.sectionId
            ? {
                ...section,
                fields: section.fields.map(f =>
                  f.id === fieldEditor.field?.id ? field : f
                ),
              }
            : section
        ),
      }));
      toast.success('Field updated');
    }

    setFieldEditor({ isOpen: false, mode: 'add', sectionId: '' });
  };

  const deleteField = (sectionId: string, fieldId: string) => {
    if (!confirm('Are you sure you want to delete this field?')) return;

    setFormConfig(prev => ({
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.filter(f => f.id !== fieldId),
            }
          : section
      ),
    }));
    toast.success('Field deleted');
  };

  const moveFieldUp = (sectionId: string, fieldId: string) => {
    setFormConfig(prev => ({
      sections: prev.sections.map(section => {
        if (section.id !== sectionId) return section;

        const fields = [...section.fields];
        const index = fields.findIndex(f => f.id === fieldId);
        if (index > 0) {
          [fields[index - 1], fields[index]] = [fields[index], fields[index - 1]];
          fields.forEach((f, i) => { f.order = i + 1; });
        }
        return { ...section, fields };
      }),
    }));
  };

  const moveFieldDown = (sectionId: string, fieldId: string) => {
    setFormConfig(prev => ({
      sections: prev.sections.map(section => {
        if (section.id !== sectionId) return section;

        const fields = [...section.fields];
        const index = fields.findIndex(f => f.id === fieldId);
        if (index < fields.length - 1) {
          [fields[index], fields[index + 1]] = [fields[index + 1], fields[index]];
          fields.forEach((f, i) => { f.order = i + 1; });
        }
        return { ...section, fields };
      }),
    }));
  };

  const moveFieldToSection = (fromSectionId: string, fieldId: string, toSectionId: string) => {
    if (fromSectionId === toSectionId) return;

    setFormConfig(prev => {
      let fieldToMove: FormField | undefined;

      // Remove field from source section
      const updatedSections = prev.sections.map(section => {
        if (section.id === fromSectionId) {
          const fields = section.fields.filter(f => {
            if (f.id === fieldId) {
              fieldToMove = f;
              return false;
            }
            return true;
          });
          fields.forEach((f, i) => { f.order = i + 1; });
          return { ...section, fields };
        }
        return section;
      });

      // Add field to target section
      if (fieldToMove) {
        return {
          sections: updatedSections.map(section => {
            if (section.id === toSectionId) {
              const fields: FormField[] = [...section.fields, { ...fieldToMove, order: section.fields.length + 1 } as FormField];
              return { ...section, fields };
            }
            return section;
          })
        };
      }

      return { sections: updatedSections };
    });

    toast.success('Field moved successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-800">
              {id === 'new' ? 'Tạo Biểu Mẫu Mới' : 'Chỉnh Sửa Biểu Mẫu'}
            </h1>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/pathology/patients/form-builder')}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                ← Back
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : '💾 Save Template'}
              </button>
            </div>
          </div>

          {/* Template metadata */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g. Standard Patient Information Form"
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={templateDesc}
                onChange={(e) => setTemplateDesc(e.target.value)}
                placeholder="Description of this template"
                rows={2}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-bold text-blue-800 mb-2">📋 Instructions:</h3>
          <ul className="text-sm text-blue-700 space-y-1 ml-4 list-disc">
            <li>Add sections to organize form fields</li>
            <li>Add fields within each section</li>
            <li>Use ⬆️⬇️ buttons to reorder sections and fields</li>
            <li>Click "Save Template" to save your changes</li>
          </ul>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {formConfig.sections.map((section, index) => (
            <div
              key={section.id}
              className="bg-white rounded-lg shadow-md border-l-4 border-blue-500"
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800">
                      {section.order}. {section.title}
                    </h3>
                    {section.description && (
                      <p className="text-sm text-gray-600 mt-1">{section.description}</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => moveSectionUp(section.id)}
                      disabled={index === 0}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-30"
                      title="Move up"
                    >
                      ⬆️
                    </button>
                    <button
                      onClick={() => moveSectionDown(section.id)}
                      disabled={index === formConfig.sections.length - 1}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-30"
                      title="Move down"
                    >
                      ⬇️
                    </button>
                    <button
                      onClick={() => openEditSectionModal(section)}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      title="Edit section"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => deleteSection(section.id)}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                      title="Delete section"
                    >
                      🗑️
                    </button>
                    <button
                      onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                    >
                      {expandedSection === section.id ? '▲' : '▼'}
                    </button>
                  </div>
                </div>

                {expandedSection === section.id && (
                  <div className="mt-4 pt-4 border-t space-y-2">
                    <div className="flex justify-between items-center mb-3">
                      <p className="text-sm text-gray-600 font-medium">
                        Fields in this section ({section.fields.length}):
                      </p>
                      <button
                        onClick={() => openAddFieldModal(section.id)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        ➕ Add Field
                      </button>
                    </div>

                    {section.fields.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No fields yet. Click "Add Field" to create one.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {section.fields.map((field, fieldIndex) => (
                          <div
                            key={field.id}
                            className="ml-6 p-3 bg-gray-50 rounded flex items-center justify-between group"
                          >
                            <div>
                              <span className="font-medium text-gray-700">{field.label}</span>
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                              <span className="text-xs text-gray-400 ml-2">({field.type})</span>
                            </div>
                            <div className="flex space-x-1 opacity-0 group-hover:opacity-100">
                              <button
                                onClick={() => moveFieldUp(section.id, field.id)}
                                disabled={fieldIndex === 0}
                                className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-30"
                                title="Move up"
                              >
                                ▲
                              </button>
                              <button
                                onClick={() => moveFieldDown(section.id, field.id)}
                                disabled={fieldIndex === section.fields.length - 1}
                                className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-30"
                                title="Move down"
                              >
                                ▼
                              </button>
                              {formConfig.sections.length > 1 && (
                                <select
                                  onChange={(e) => moveFieldToSection(section.id, field.id, e.target.value)}
                                  className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 cursor-pointer"
                                  title="Move to section"
                                  value=""
                                >
                                  <option value="" disabled>Move to...</option>
                                  {formConfig.sections
                                    .filter(s => s.id !== section.id)
                                    .map(s => (
                                      <option key={s.id} value={s.id}>
                                        {s.title}
                                      </option>
                                    ))}
                                </select>
                              )}
                              <button
                                onClick={() => openEditFieldModal(section.id, field)}
                                className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                title="Edit"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => deleteField(section.id, field.id)}
                                className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {formConfig.sections.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-gray-500 mb-4">No sections yet. Start by adding a section.</p>
            </div>
          )}

          <button
            onClick={openAddSectionModal}
            className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
          >
            ➕ Add Section
          </button>
        </div>

        {/* Summary */}
        <div className="mt-6 bg-white rounded-lg shadow-md p-6">
          <h3 className="font-bold text-gray-800 mb-3">📊 Summary:</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-green-50 rounded">
              <div className="text-2xl font-bold text-green-600">
                {formConfig.sections.length}
              </div>
              <div className="text-sm text-gray-600">Sections</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded">
              <div className="text-2xl font-bold text-blue-600">
                {formConfig.sections.reduce((sum, s) => sum + s.fields.length, 0)}
              </div>
              <div className="text-sm text-gray-600">Total Fields</div>
            </div>
          </div>
        </div>

        {/* Section Editor Modal */}
        {sectionEditor.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-4">
                  {sectionEditor.mode === 'add' ? '➕ Add Section' : '✏️ Edit Section'}
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Section ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editingSection.id || ''}
                      onChange={(e) => setEditingSection({ ...editingSection, id: e.target.value })}
                      placeholder="e.g. personal_info"
                      className="w-full px-3 py-2 border rounded"
                      disabled={sectionEditor.mode === 'edit'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editingSection.title || ''}
                      onChange={(e) => setEditingSection({ ...editingSection, title: e.target.value })}
                      placeholder="e.g. Personal Information"
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={editingSection.description || ''}
                      onChange={(e) => setEditingSection({ ...editingSection, description: e.target.value })}
                      placeholder="Optional description"
                      rows={2}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={editingSection.collapsible || false}
                      onChange={(e) => setEditingSection({ ...editingSection, collapsible: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label className="text-sm text-gray-700">Collapsible (can be collapsed/expanded)</label>
                  </div>

                  {editingSection.collapsible && (
                    <div className="flex items-center space-x-2 ml-6">
                      <input
                        type="checkbox"
                        checked={editingSection.collapsed_by_default || false}
                        onChange={(e) => setEditingSection({ ...editingSection, collapsed_by_default: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <label className="text-sm text-gray-700">Collapsed by default</label>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                  <button
                    onClick={() => setSectionEditor({ isOpen: false, mode: 'add' })}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveSection}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    {sectionEditor.mode === 'add' ? 'Add Section' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Field Editor Modal */}
        {fieldEditor.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-4">
                  {fieldEditor.mode === 'add' ? '➕ Add Field' : '✏️ Edit Field'}
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editingField.id || ''}
                      onChange={(e) => setEditingField({ ...editingField, id: e.target.value })}
                      placeholder="e.g. patient_name"
                      className="w-full px-3 py-2 border rounded"
                      disabled={fieldEditor.mode === 'edit'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Label <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editingField.label || ''}
                      onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                      placeholder="e.g. Patient Name"
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field Type
                    </label>
                    <select
                      value={editingField.type || 'text'}
                      onChange={(e) => setEditingField({ ...editingField, type: e.target.value as any })}
                      className="w-full px-3 py-2 border rounded"
                    >
                      <option value="text">Text</option>
                      <option value="textarea">Textarea</option>
                      <option value="number">Number</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="date">Date</option>
                      <option value="datetime">Date Time</option>
                      <option value="select">Select (Dropdown)</option>
                      <option value="checkbox">Checkbox</option>
                      <option value="radio">Radio</option>
                    </select>
                  </div>

                  {(editingField.type === 'select' || editingField.type === 'radio') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Options (JSON format)
                      </label>
                      <textarea
                        value={JSON.stringify(editingField.options || [], null, 2)}
                        onChange={(e) => {
                          try {
                            const options = JSON.parse(e.target.value);
                            setEditingField({ ...editingField, options });
                          } catch (err) {
                            // Invalid JSON
                          }
                        }}
                        placeholder='[{"value":"option1","label":"Option 1"}]'
                        className="w-full px-3 py-2 border rounded font-mono text-sm"
                        rows={4}
                      />
                    </div>
                  )}

                  {editingField.type === 'textarea' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rows
                      </label>
                      <input
                        type="number"
                        value={editingField.rows || 3}
                        onChange={(e) => setEditingField({ ...editingField, rows: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border rounded"
                        min="1"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Placeholder
                    </label>
                    <input
                      type="text"
                      value={editingField.placeholder || ''}
                      onChange={(e) => setEditingField({ ...editingField, placeholder: e.target.value })}
                      placeholder="e.g. Enter name..."
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Help Text
                    </label>
                    <input
                      type="text"
                      value={editingField.help_text || ''}
                      onChange={(e) => setEditingField({ ...editingField, help_text: e.target.value })}
                      placeholder="Optional help text"
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={editingField.required || false}
                      onChange={(e) => setEditingField({ ...editingField, required: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <label className="text-sm text-gray-700">Required field</label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                  <button
                    onClick={() => setFieldEditor({ isOpen: false, mode: 'add', sectionId: '' })}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveField}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    {fieldEditor.mode === 'add' ? 'Add Field' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
